import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { NeumorphicCard } from "components/layout/NeumorphicCard";
import Agent from "./Agent";

interface LandingProps {
  scrollSpeed: number;
}

export default function Landing({ scrollSpeed }: LandingProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    // === OUTER SECTION CONTAINER (sets background) ===
    <Box
      sx={{
        width: "100vw",
        height: "100vh", // ✅ Ensure full screen
        backgroundImage: 'url("/textures/virusgoku_hd.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        px: theme.spacing(6),
      }}
    >
      {/* === WRAPPER TO PUSH CONTENT 1/3 FROM TOP === */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%", // ✅ Full height of the outer container
          justifyContent: "flex-start",
        }}
      >
        {/* === Spacer pushes everything down by 1/3 of screen height === */}
        <Box sx={{ height: { xs: "15vh", md: "30vh" }, flexShrink: 0 }} />

        {/* === MAIN CONTENT (fills the remaining 2/3) === */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            // justifyContent: "center",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            flex: 1,
            flexWrap: "wrap",
            gap: theme.spacing(4),
            overflow: "hidden",
          }}
        >
          {/* === LEFT TEXT PANEL === */}
          <Box
            sx={{
              flex: 4,
              minWidth: { xs: "100%", md: 0 },
              px: { xs: 3, md: 8 },
              py: { xs: 2, md: 0 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              textAlign: { xs: "center", md: "left" },
              color: "white",
              alignSelf: "flex-start",
              height: "100%",
              overflow: "hidden", // ✅ never scroll
              minHeight: 0, // ✅ allow shrink if needed
              flexShrink: 1, // ✅ allow this box to shrink to fit screen
            }}
          >
            <NeumorphicCard
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                width: "auto",
                height: "auto",
                p: theme.spacing(3),
                boxSizing: "border-box",
                m: 0,

                // ✅ allow wrapping/scrolling properly on small screens
                minHeight: "fit-content",
                overflow: "visible",
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  // fontStyle: "italic",
                  mb: 1,
                  // color: theme.palette.secondary.main,
                  fontSize: "clamp(1.5rem, 6vw, 3rem)",
                }}
              >
                Run a Company without Employees
              </Typography>

              {/* <Typography
              variant="h3"
              sx={{
                fontFamily: "SuperBrigade",
                mb: 2,
                fontSize: "clamp(1.5rem, 6vw, 3rem)", // ✅ responsive heading
                textShadow: "1px 1px 2px rgba(0, 0, 0, 0.25)",
                wordBreak: "break-word",
              }}
            >
              STAFFBOTICS
            </Typography> */}

              <Typography
                variant="subtitle1"
                sx={{
                  mt: theme.spacing(4),
                  lineHeight: 1.4,
                  fontSize: "clamp(1rem, 2.5vw, 1rem)", // ✅ shrink on small screens
                  // color: theme.palette.secondary.main,
                  wordBreak: "break-word",
                }}
              >
                We automate Small and Medium Enterprise operations with a
                workforce of AI agents; coordinated, transparent, and led by
                you.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mt: theme.spacing(2),
                  lineHeight: 1.4,
                  fontSize: "clamp(1rem, 2.5vw, 1rem)",
                  wordBreak: "break-word",
                  display: { xs: "none", md: "block" }, // 👈 only show on md and up
                }}
              >
                Agentic systems, not generic SaaS. Our agents coordinate, plan,
                and communicate. Every action is logged, auditable, and
                purpose-driven.
              </Typography>
            </NeumorphicCard>
          </Box>

          {/* === RIGHT 3D AGENT SCENE === */}
          <Box
            sx={{
              flex: 6,
              minWidth: { xs: "100%", md: 0 },
              height: "100%", // ✅ Take full vertical space
              maxWidth: "100%", // ✅ Prevent horizontal overflow
              overflow: "hidden", // ✅ Clip any excess from Agent canvas
              alignSelf: "flex-start", // ✅ Align itself to top of parent flex container
              display: "flex", // ✅ Important: control child alignment
              alignItems: "flex-start", // ✅ Align Agent to top within this box
            }}
          >
            <Agent scrollSpeed={scrollSpeed} sizeRatio={2} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
